import * as React from "react";
import Button from "../Button";
import { NetsuiteOrderLookupWidget, NetsuiteSalesOrder } from "../../types";
import NetsuiteOrderService from "../../services/NetsuiteOrderService";
import StoreContainer from "../../stores/StoreContainer";

interface Props {
  config: NetsuiteOrderLookupWidget;
}

export default function NetsuiteOrderLookupWidgetView(props: Props) {
  const [orders, setOrders] = React.useState<
    Array<NetsuiteSalesOrder> | undefined
  >();
  const [order, setOrder] = React.useState<NetsuiteSalesOrder | undefined>();
  const [email, setEmail] = React.useState("");
  const [isAmazonOrder, setIsAmazonOrder] = React.useState(false);
  const [zipCode, setZipcode] = React.useState("");
  const [ordernumber, setOrdernumber] = React.useState("");
  const [fetching, setFetching] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const mountedRef = React.useRef(true);

  const fetchOrder = React.useCallback(async (id: string) => {
    setFetching(true);
    const result = await NetsuiteOrderService.getOrder(
      StoreContainer.SettingStore.getSiteId(),
      id
    );
    setOrder(result);
    setFetching(false);
  }, []);

  const renderOrder = React.useCallback(
    (order: NetsuiteSalesOrder) => {
      return (
        <div key={order.id}>
          <a
            href={order.id}
            onClick={(e) => {
              e.preventDefault();
              fetchOrder(order.id);
            }}
          >
            {order.values.datecreated}
          </a>
        </div>
      );
    },
    [fetchOrder]
  );

  const renderShipDate = React.useCallback((shipped: string | undefined) => {
    const shipdate = new Date(shipped);
    if (shipdate === undefined) {
      return "unknown";
    }

    return shipdate.toLocaleDateString();
  }, []);

  const fetchOrders = React.useCallback(async () => {
    const amazonRegex = new RegExp("\\d{3}-\\d{7}-\\d{7}", "g");
    if (amazonRegex.test(ordernumber)) {
      setIsAmazonOrder(true);
      setFetching(false);
      return;
    }

    let response: any[] = null;
    try {
      response = await NetsuiteOrderService.lookup(
        StoreContainer.SettingStore.getSiteId(),
        email,
        zipCode,
        ordernumber
      );
      setOrders(response);
      setFetching(false);
      if (!mountedRef.current) {
        return;
      }
    } catch (e) {
      if (!mountedRef.current) {
        return;
      }
      console.error(e);
    }
  }, [email, zipCode, ordernumber]);

  const renderContent = React.useCallback(() => {
    if (order !== undefined) {
      return (
        <>
          <div className="velaro-form-group">
            <label>Tracking numbers</label>
            <p>{order.linkedtrackingnumbers || "none"}</p>
          </div>
          <div className="velaro-form-group">
            <label>Ship date</label>
            <p>{renderShipDate(order.shipdate)}</p>
          </div>
          <div className="velaro-form-group">
            <label>Shipping method</label>
            <p>{order.shipcarrier || "unknown"}</p>
          </div>
        </>
      );
    }

    if (isAmazonOrder) {
      return (
        <>
          <div>{props.config.AmazonResultErrorMessage}</div>
          <div style={{ marginTop: "15px" }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsAmazonOrder(false);
              }}
            >
              Go Back
            </a>
          </div>
        </>
      );
    }

    if (orders && orders.length && fetching) {
      return <>Loading Details...</>;
    }

    if (orders && orders.length) {
      return <>{orders.map(renderOrder)}</>;
    }

    if (orders && orders.length === 0) {
      return (
        <>
          <div>{props.config.NoResultsErrorMessage}</div>
          <div style={{ marginTop: "15px" }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setOrders(undefined);
              }}
            >
              Go Back
            </a>
          </div>
        </>
      );
    }

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email.length <= 3 || (!zipCode && !ordernumber)) {
            setErrorMessage(props.config.InvalidEntryErrorText);
          } else {
            setErrorMessage("");
            setFetching(true);
            fetchOrders();
          }
        }}
      >
        <div style={{ textAlign: "center" }}>
          <small
            className="widget-message"
            style={{ fontWeight: "lighter", color: "red" }}
          >
            {errorMessage}
          </small>
        </div>
        <div className="velaro-form-group">
          <label>{props.config.EmailLabelText}</label>
          <input
            minLength={3}
            type="text"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            aria-label={props.config.EmailLabelText}
          />
        </div>
        <div className="velaro-form-group" style={{ marginTop: "30px" }}>
          <label>{props.config.ZipCodeLabelText}</label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) => setZipcode(e.currentTarget.value)}
            aria-label={props.config.ZipCodeLabelText}
          />
        </div>
        <div style={{ textAlign: "center" }}>
          <label style={{ fontWeight: "bolder" }}>OR</label>
        </div>
        <div className="velaro-form-group">
          <label>{props.config.OrderNumberLabelText}</label>
          <input
            type="text"
            value={ordernumber}
            onChange={(e) => setOrdernumber(e.currentTarget.value)}
            aria-label={props.config.OrderNumberLabelText}
          />
        </div>
        <Button
          disabled={fetching}
          type="submit"
          className="landing-widget-button"
          variant="primary"
        >
          {fetching ? "Loading..." : props.config.LookupButtonText}
        </Button>
      </form>
    );
  }, [
    order,
    isAmazonOrder,
    orders,
    fetching,
    errorMessage,
    props.config.EmailLabelText,
    props.config.ZipCodeLabelText,
    props.config.OrderNumberLabelText,
    props.config.LookupButtonText,
    props.config.AmazonResultErrorMessage,
    props.config.NoResultsErrorMessage,
    props.config.InvalidEntryErrorText,
    email,
    zipCode,
    ordernumber,
    renderShipDate,
    renderOrder,
    fetchOrders
  ]);

  const goBack = React.useCallback(
    (e: any) => {
      e.preventDefault();

      if (order !== undefined) {
        setOrder(undefined);
        return;
      }

      setOrders(undefined);
    },
    [order, setOrder, setOrders]
  );

  return (
    <div className="landing-widget">
      <div className="widget-body">
        <div className="widget-message" style={{ paddingBottom: "5px" }}>
          {props.config.LabelText}
        </div>
        {(order === undefined || !orders || !orders.length) && (
          <small className="widget-message" style={{ fontWeight: "lighter" }}>
            {props.config.SubLabelText}
          </small>
        )}
        {(order !== undefined || (orders && orders.length > 0)) && (
          <div style={{ position: "relative" }}>
            <a
              href="#"
              onClick={(e) => goBack(e)}
              style={{
                textDecoration: "none",
                position: "absolute",
                right: "10px",
                top: "10px"
              }}
            >
              Go Back
            </a>
          </div>
        )}
        <div className="widget-form-container">{renderContent()}</div>
      </div>
    </div>
  );
}
